// This validator is deliberately maintained as a byte-identical mirror in
// frontend/gateway/src/plcCapabilityManifest.js and
// frontend/cseditor/src/plc/plcCapabilityManifest.js (the two bundles share
// no build infrastructure). Each frontend's test suite asserts the copies
// match — edit both files together.
const TARGETS = new Set(["cloud_control_plane", "local_edge_runtime"]);
const STATUSES = new Set(["available", "configuration_only", "planned"]);

export function assertPLCCapabilityManifest(manifest, { allowLegacy = true } = {}) {
  if (!manifest || typeof manifest !== "object" || Array.isArray(manifest)) {
    throw new Error("PLC capability manifest is not an object.");
  }
  const version = Number(manifest.version);
  if (!Number.isInteger(version) || version < 1) {
    throw new Error("PLC capability manifest version is missing or invalid.");
  }
  if (version > 3) {
    throw new Error(`PLC capability manifest v${version} is newer than this client supports.`);
  }
  if (version < 3) {
    if (!allowLegacy) throw new Error(`PLC capability manifest v${version} is not accepted here.`);
    return manifest;
  }
  if (!TARGETS.has(manifest.target)) {
    throw new Error("PLC capability manifest target is missing or invalid.");
  }
  const scope = manifest.scope;
  if (
    !scope ||
    scope.kind !== "host_build" ||
    typeof scope.host_os !== "string" || !scope.host_os.trim() ||
    typeof scope.host_architecture !== "string" || !scope.host_architecture.trim() ||
    scope.remote_runtime_selected !== false
  ) {
    throw new Error("PLC capability manifest host/build scope is missing or invalid.");
  }
  if (!Array.isArray(manifest.areas) || !Array.isArray(manifest.stages) || !Array.isArray(manifest.capabilities)) {
    throw new Error("PLC capability manifest catalogs are incomplete.");
  }
  manifest.capabilities.forEach((capability, index) => {
    if (!capability || typeof capability.id !== "string" || !capability.id.trim()) {
      throw new Error(`PLC capability at index ${index} has no stable id.`);
    }
    if (!STATUSES.has(capability.status) || typeof capability.executable !== "boolean") {
      throw new Error(`PLC capability "${capability.id}" has incomplete execution truth.`);
    }
    if ((capability.status === "available") !== capability.executable) {
      throw new Error(`PLC capability "${capability.id}" contradicts its executable flag.`);
    }
  });
  return manifest;
}

export default assertPLCCapabilityManifest;
